import { createElement } from 'react';
import { pdf } from '@react-pdf/renderer';
import createDownloadLink from 'helpers/createDownloadLink';
import InvoicePdfDocument from './InvoicePdf/document';
import PrestigeStickerDocument from './InvoicePdf/prestigeStickerDocument';
import { isPrestigeInvoiceAgent } from './InvoicePdf/prestige';
import {
    encodeStickerQrValue,
    getPrestigeStickerModel,
    getPrestigeStickerQrPayload,
    stickerPdfAuthor,
} from './InvoicePdf/prestigeSticker';
import { renderStickerQrDataUrl } from './InvoicePdf/stickerQr';
import {
    invoiceDownloadFileName,
    invoiceWithInlinedImages,
    measureInvoiceImageSizes,
} from './invoiceImages';
import { invoiceWithCreator } from './invoiceCreator';
import { Invoice, InvoiceAcceptance } from './types';

export const renderInvoicePdf = async (
    invoice: Invoice,
    acceptance: InvoiceAcceptance | null = null,
    agentId = '',
) => {
    const invoiceWithImages = await invoiceWithInlinedImages(
        await invoiceWithCreator(invoice, agentId),
    );
    const imageSizes = await measureInvoiceImageSizes(
        invoiceWithImages,
        acceptance?.signatureDataUrl ? [acceptance.signatureDataUrl] : [],
    );
    return pdf(
        createElement(InvoicePdfDocument, {
            invoice: invoiceWithImages,
            acceptance,
            imageSizes,
            agentId,
        }) as Parameters<typeof pdf>[0],
    ).toBlob();
};

const stickerDownloadFileName = (invoice: Invoice) => {
    const raw = ['Sticker', invoice.number].filter(Boolean).join(' ').trim();
    return `${raw.replace(/[/\\?%*:|"<>]/g, '-')}.pdf`;
};

export const renderPrestigeStickerPdf = async (
    invoice: Invoice,
    acceptance: InvoiceAcceptance | null = null,
    agentId = '',
) => {
    if (!isPrestigeInvoiceAgent(agentId)) {
        throw new Error('Stickers are only available for Prestige invoices');
    }
    const invoiceWithImages = await invoiceWithInlinedImages(invoice);
    const imageSizes = await measureInvoiceImageSizes(invoiceWithImages);
    const model = getPrestigeStickerModel(invoiceWithImages);
    const qr = await renderStickerQrDataUrl(
        encodeStickerQrValue(getPrestigeStickerQrPayload(invoiceWithImages, agentId)),
        model.logoImageUrl,
        model.logoImageUrl ? imageSizes[model.logoImageUrl] : undefined,
    );
    return pdf(
        createElement(PrestigeStickerDocument, {
            invoice: invoiceWithImages,
            imageSizes,
            qrImageUrl: qr?.dataUrl || '',
            qrPixelSize: qr ? { width: qr.width, height: qr.height } : undefined,
            author: stickerPdfAuthor(invoiceWithImages, acceptance),
        }) as Parameters<typeof pdf>[0],
    ).toBlob();
};

export const downloadPrestigeSticker = async (
    invoice: Invoice,
    acceptance: InvoiceAcceptance | null = null,
    agentId = '',
) => {
    const blob = await renderPrestigeStickerPdf(invoice, acceptance, agentId);
    createDownloadLink(URL.createObjectURL(blob), stickerDownloadFileName(invoice));
};

const downloadInvoice = async (
    invoice: Invoice,
    acceptance: InvoiceAcceptance | null = null,
    agentId = '',
) => {
    const blob = await renderInvoicePdf(invoice, acceptance, agentId);
    createDownloadLink(URL.createObjectURL(blob), invoiceDownloadFileName(invoice));
};

export default downloadInvoice;
